/**
 * The contacts page's arithmetic. No React, no fetch, no clock.
 *
 * Three things live here because each of them is a rule that is easy to get
 * subtly wrong inline and impossible to see once it is wrong: which way a
 * column sorts when you first click it, what the pager's "showing x-y of z"
 * actually is, and - the important one - what a ledger row IS.
 */
import type { ContactRow, ContactSort } from "../../../../lib/marketingContacts";

/**
 * Which direction a column opens in.
 *
 * Descending for everything countable or dated, ascending for the address.
 * "Newest contact first" and "most companies first" are the questions those
 * columns exist to answer; opening them on the oldest row or the smallest count
 * means every reader clicks twice, every time.
 */
const OPENS_DESC: Record<ContactSort, boolean> = {
  lastContacted: true,
  firstContacted: true,
  contacts: true,
  companies: true,
  added: true,
  address: false,
};

export interface SortState {
  sort: ContactSort;
  order: "asc" | "desc";
}

/**
 * What clicking a column head does.
 *
 * `SortHead` always reports its own column and leaves the decision here, which
 * means this is the only place that knows a second click on the ACTIVE column
 * reverses it while a click on any other column starts fresh. Keeping the
 * previous column's direction would be worse than it sounds: sorting by address
 * and then clicking "last contacted" would open on the oldest contact.
 */
export function nextSort(current: SortState, column: ContactSort): SortState {
  if (current.sort === column) return { sort: column, order: current.order === "asc" ? "desc" : "asc" };
  return { sort: column, order: OPENS_DESC[column] ? "desc" : "asc" };
}

/**
 * The pager's window, one-based and inclusive.
 *
 * `rowCount` is the length of the page actually returned, not the page size:
 * the last page is short, and computing `to` from the page size claims rows
 * that are not there.
 */
export function showingRange(page: number, pageSize: number, rowCount: number): { from: number; to: number } {
  if (rowCount <= 0) return { from: 0, to: 0 };
  const from = (page - 1) * pageSize + 1;
  return { from, to: from + rowCount - 1 };
}

/**
 * What one ledger row is, in the only three states a reader cares about.
 *
 * ⚠ `enrolled` IS NOT `contacted`. A `MktContact` row is created the moment a
 * campaign enrols the address, and enrolment sends nothing - it is explicitly
 * forbidden from touching the contact counters. On the local mirror on
 * 2026-08-22 that was 846 rows in the ledger and 2 ever written to. A screen
 * that draws all 846 as people we have mailed is wrong by a factor of 423, and
 * wrong in the direction that gets somebody mailed twice.
 *
 * `stopped` wins over both: an address that opted out after one letter is out
 * of every future audience, and that is the fact worth showing first.
 */
export type RowState = "stopped" | "contacted" | "enrolled";

export function rowState(row: Pick<ContactRow, "status" | "lastContactedAt">): RowState {
  if (row.status !== "active") return "stopped";
  return row.lastContactedAt ? "contacted" : "enrolled";
}

/**
 * Whether the ledger files this mailbox under a different string than the one
 * on screen.
 *
 * True for gmail addresses with dots, and for plus-tagged mailboxes on the
 * thirteen consumer domains `emailKey` strips tags for - NOT for a plus-tag on
 * an ordinary company domain, where `info+news@firma.ee` stays its own key.
 * Measured 2026-08-22 on the local ledger: 3 of the first rows checked differ,
 * all of them dotted gmail addresses.
 *
 * When it is true the detail view has to say so: the two strings look like two
 * mailboxes and are one, and an operator comparing the address on screen
 * against the register's spelling would otherwise conclude the history belongs
 * to somebody else.
 */
export function keyDiffers(address: string, emailKey: string): boolean {
  return address.trim().toLowerCase() !== emailKey;
}
